import React, { useMemo, useState } from 'react';
import { ArrowLeft, FileText, Plus, Save, Trash2, Wrench } from 'lucide-react';
import { formatPrice } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

export const SERVICE_LINE_CATEGORIES = [
  { value: 'prestation', label: 'Prestation' },
  { value: 'main_oeuvre', label: "Main d'oeuvre" },
  { value: 'piece', label: 'Pièce' },
  { value: 'peinture', label: 'Peinture' },
  { value: 'entretien', label: 'Entretien' },
  { value: 'reparation', label: 'Réparation' },
  { value: 'revision', label: 'Révision' },
];

const createLineId = () => `srv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const createEmptyServiceLine = () => ({
  id: createLineId(),
  catalog_item_id: null,
  designation: '',
  category: 'prestation',
  description: '',
  quantity: 1,
  unit: 'u',
  unit_price: 0,
  line_total: 0,
});

export const normalizeServiceLines = (lines = []) =>
  (Array.isArray(lines) ? lines : []).map((line) => {
    const quantity = Number(line.quantity || 0);
    const unitPrice = Number(line.unit_price || 0);
    return {
      id: line.id || createLineId(),
      catalog_item_id: line.catalog_item_id || null,
      designation: line.designation || '',
      category: line.category || 'prestation',
      description: line.description || '',
      quantity,
      unit: line.unit || 'u',
      unit_price: unitPrice,
      line_total: quantity * unitPrice,
    };
  });

export const calculateServiceQuoteTotals = ({ service_lines = [], discount = 0, discount_type = 'percent' }) => {
  const subtotal = normalizeServiceLines(service_lines).reduce((sum, line) => sum + line.line_total, 0);
  const discountAmount = discount_type === 'percent'
    ? subtotal * (Number(discount || 0) / 100)
    : Number(discount || 0);
  return {
    serviceLinesTotal: subtotal,
    subtotal,
    discountAmount,
    total: subtotal - discountAmount,
  };
};

const buildLineFromCatalog = (item) => ({
  id: createLineId(),
  catalog_item_id: item.id,
  designation: item.name,
  category: item.category || 'prestation',
  description: item.description || '',
  quantity: 1,
  unit: item.default_unit || 'u',
  unit_price: Number(item.default_price || 0),
  line_total: Number(item.default_price || 0),
});

export const ServiceQuoteEditor = ({
  title,
  subtitle,
  reference,
  clients,
  serviceCatalog,
  form,
  setForm,
  onBack,
  onSubmit,
  submitting,
  submitLabel,
}) => {
  const [catalogSelection, setCatalogSelection] = useState('');
  const selectedClient = clients.find((client) => client.id === form.client_id);

  const lines = useMemo(() => normalizeServiceLines(form.service_lines), [form.service_lines]);
  const totals = useMemo(
    () => calculateServiceQuoteTotals({
      service_lines: lines,
      discount: form.discount,
      discount_type: form.discount_type,
    }),
    [lines, form.discount, form.discount_type]
  );

  const updateLine = (lineId, key, value) => {
    setForm((prev) => ({
      ...prev,
      service_lines: normalizeServiceLines(prev.service_lines).map((line) =>
        line.id === lineId
          ? {
              ...line,
              [key]: key === 'quantity' || key === 'unit_price' ? Number(value || 0) : value,
            }
          : line
      ),
    }));
  };

  const addEmptyLine = () => {
    setForm((prev) => ({
      ...prev,
      service_lines: [...normalizeServiceLines(prev.service_lines), createEmptyServiceLine()],
    }));
  };

  const addCatalogLine = (itemId) => {
    const item = serviceCatalog.find((entry) => entry.id === itemId);
    if (!item) return;
    setForm((prev) => ({
      ...prev,
      service_lines: [...normalizeServiceLines(prev.service_lines), buildLineFromCatalog(item)],
    }));
    setCatalogSelection('');
  };

  const removeLine = (lineId) => {
    setForm((prev) => ({
      ...prev,
      service_lines: normalizeServiceLines(prev.service_lines).filter((line) => line.id !== lineId),
    }));
  };

  return (
    <div className="space-y-6" data-testid="service-quote-editor">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
              {title}
            </h1>
            <p className="text-slate-500 mt-1">{reference || subtitle}</p>
          </div>
        </div>
        <Button
          onClick={onSubmit}
          disabled={submitting}
          className="bg-yellow-500 hover:bg-yellow-600 text-slate-900"
        >
          <Save className="w-4 h-4 mr-2" />
          {submitLabel}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-slate-200">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <FileText className="w-5 h-5 text-yellow-500" />
                Client
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <Select value={form.client_id} onValueChange={(value) => setForm((prev) => ({ ...prev, client_id: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name} {client.company && `(${client.company})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedClient && (
                <div className="p-3 bg-slate-50 rounded-md">
                  <p className="font-medium text-slate-800">{selectedClient.name}</p>
                  {selectedClient.company && <p className="text-slate-500">{selectedClient.company}</p>}
                  {selectedClient.email && <p className="text-sm text-slate-500">{selectedClient.email}</p>}
                  {selectedClient.phone && <p className="text-sm text-slate-500">{selectedClient.phone}</p>}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <Wrench className="w-5 h-5 text-yellow-500" />
                Catalogue prestation / pièces
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="flex flex-col md:flex-row gap-3">
                <Select value={catalogSelection} onValueChange={addCatalogLine}>
                  <SelectTrigger className="md:flex-1">
                    <SelectValue placeholder="Ajouter une ligne depuis le catalogue" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceCatalog.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name} - {formatPrice(item.default_price || 0)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" onClick={addEmptyLine}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ligne libre
                </Button>
              </div>

              <div className="space-y-4">
                {lines.length === 0 && (
                  <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-slate-500">
                    Ajoute une prestation, de la main d&apos;oeuvre ou une pièce pour commencer.
                  </div>
                )}

                {lines.map((line, index) => (
                  <div key={line.id} className="rounded-xl border border-slate-200 bg-white p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">Ligne {index + 1}</Badge>
                        <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">
                          {SERVICE_LINE_CATEGORIES.find((entry) => entry.value === line.category)?.label || line.category}
                        </Badge>
                      </div>
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeLine(line.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label>Désignation</Label>
                        <Input
                          value={line.designation}
                          onChange={(event) => updateLine(line.id, 'designation', event.target.value)}
                          placeholder="Ex: Révision 500h, Main d'oeuvre atelier, Flexible hydraulique..."
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Catégorie</Label>
                        <Select value={line.category} onValueChange={(value) => updateLine(line.id, 'category', value)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {SERVICE_LINE_CATEGORIES.map((category) => (
                              <SelectItem key={category.value} value={category.value}>
                                {category.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Unité</Label>
                        <Input
                          value={line.unit}
                          onChange={(event) => updateLine(line.id, 'unit', event.target.value)}
                          placeholder="h, u, forfait..."
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Quantité</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.quantity}
                          onChange={(event) => updateLine(line.id, 'quantity', event.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Prix unitaire HT</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.unit_price}
                          onChange={(event) => updateLine(line.id, 'unit_price', event.target.value)}
                        />
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <Label>Description</Label>
                        <Textarea
                          value={line.description}
                          onChange={(event) => updateLine(line.id, 'description', event.target.value)}
                          placeholder="Détail complémentaire de la ligne"
                          rows={2}
                        />
                      </div>
                    </div>

                    <div className="flex justify-end text-sm text-slate-600">
                      <span className="mr-2">Total ligne :</span>
                      <span className="font-mono font-semibold text-slate-900">{formatPrice(line.line_total)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Notes et conditions</CardTitle>
            </CardHeader>
            <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Validité du devis</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.validity_days}
                  onChange={(event) => setForm((prev) => ({ ...prev, validity_days: Number(event.target.value || 0) }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Type de remise</Label>
                <Select
                  value={form.discount_type}
                  onValueChange={(value) => setForm((prev) => ({ ...prev, discount_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Pourcentage</SelectItem>
                    <SelectItem value="fixed">Montant fixe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Remise</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.discount}
                  onChange={(event) => setForm((prev) => ({ ...prev, discount: Number(event.target.value || 0) }))}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={form.notes}
                  onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
                  placeholder="Conditions, délais, remarques atelier..."
                  rows={5}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="border-slate-200 sticky top-6">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Récapitulatif</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <div className="flex justify-between text-slate-600">
                <span>Lignes ({lines.length})</span>
                <span className="font-mono">{formatPrice(totals.serviceLinesTotal)}</span>
              </div>
              <Separator />
              <div className="flex justify-between text-slate-600">
                <span>Sous-total HT</span>
                <span className="font-mono font-semibold">{formatPrice(totals.subtotal)}</span>
              </div>
              {totals.discountAmount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Remise</span>
                  <span className="font-mono">-{formatPrice(totals.discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between bg-slate-800 text-white p-4 rounded-lg">
                <span className="font-bold" style={{ fontFamily: 'Barlow Condensed' }}>TOTAL TTC</span>
                <span className="text-xl font-mono font-bold">{formatPrice(totals.total)}</span>
              </div>
              <div className="text-xs text-slate-500">
                Ce modèle est pensé pour les prestations, réparations, peintures, révisions, pièces et main d&apos;oeuvre.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export const ServiceQuoteDetailSections = ({ devis }) => {
  const lines = normalizeServiceLines(devis?.service_lines);

  return (
    <>
      <div className="mb-8">
        <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
          <Wrench className="w-5 h-5 text-yellow-500" />
          PRESTATIONS ET FOURNITURES
        </h3>
        <div className="space-y-3">
          {lines.map((line, index) => (
            <div key={line.id || index} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-lg font-semibold text-slate-900">{line.designation}</p>
                    <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">
                      {SERVICE_LINE_CATEGORIES.find((entry) => entry.value === line.category)?.label || line.category}
                    </Badge>
                  </div>
                  {line.description && (
                    <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{line.description}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">
                    {line.quantity} {line.unit} x {formatPrice(line.unit_price)}
                  </p>
                  <p className="text-xl font-mono font-bold text-slate-900">{formatPrice(line.line_total)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {devis.notes && (
        <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <h3 className="text-sm font-bold text-amber-800 mb-2 uppercase tracking-wide">Notes</h3>
          <p className="text-amber-900 whitespace-pre-wrap">{devis.notes}</p>
        </div>
      )}

      <div className="border-t-2 border-slate-200 pt-6">
        <div className="max-w-md ml-auto">
          <div className="space-y-3">
            <div className="flex justify-between text-slate-600">
              <span>Prestations / pièces</span>
              <span className="font-mono">{formatPrice(devis.service_lines_total || devis.subtotal || 0)}</span>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-slate-600">Sous-total HT</span>
              <span className="font-mono font-semibold">{formatPrice(devis.subtotal)}</span>
            </div>
            {devis.discount_amount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Remise ({devis.discount}{devis.discount_type === 'percent' ? '%' : '€'})</span>
                <span className="font-mono">-{formatPrice(devis.discount_amount)}</span>
              </div>
            )}
            <div className="flex justify-between bg-slate-800 text-white p-4 rounded-lg -mx-2">
              <span className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                TOTAL TTC
              </span>
              <span className="text-2xl font-mono font-bold">
                {formatPrice(devis.total)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
